import React, { memo, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import './FeaturesPage.css';

// Memoized FeatureItem component for better performance
const FeatureItem = memo(({ feature, index }) => (
  <div className="feature-list-item">
    <span className="feature-check">✓</span>
    <span className="feature-text">{feature}</span>
  </div>
));

// Memoized FeatureCategory component
const FeatureCategory = memo(({ category, index }) => (
  <div className={`feature-category ${index % 2 === 0 ? 'left' : 'right'}`}>
    <div className="feature-category-header">
      <div className="feature-category-icon">
        {category.icon}
      </div>
      <div className="feature-category-content">
        <h3 className="feature-category-title">{category.title}</h3>
        <p className="feature-category-description">{category.description}</p>
      </div>
    </div>
    <div className="feature-list">
      {category.features.map((feature, featureIndex) => (
        <FeatureItem key={featureIndex} feature={feature} index={featureIndex} />
      ))}
    </div>
  </div>
));

const FeaturesPage = memo(() => {
  const featureCategories = useMemo(() => [
    {
      id: 1,
      title: "Randevu Yönetimi",
      icon: "📅",
      description: "Gelişmiş randevu yönetim sistemi ile işlerinizi organize edin",
      features: [
        "Haftalık ve aylık takvim görünümü",
        "Randevu çakışma kontrolü",
        "Müşteri bilgileri ve geçmiş randevular",
        "SMS ve email bildirimler",
        "Personel bazında randevu dağılımı",
        "Randevu iptal ve değişiklik yönetimi"
      ]
    },
    {
      id: 2,
      title: "Müşteri Yönetimi",
      icon: "👥",
      description: "Müşterilerinizi tanıyın, sadakatlerini artırın",
      features: [
        "Detaylı müşteri profilleri",
        "Randevu geçmişi ve tercihleri",
        "Müşteri notları ve özel istekler",
        "Doğum günü ve özel gün hatırlatıcıları",
        "Müşteri segmentasyonu",
        "Sadakat puanı sistemi"
      ]
    },
    {
      id: 3,
      title: "Kasa ve Muhasebe",
      icon: "💰",
      description: "Mali işlerinizi profesyonel şekilde yönetin",
      features: [
        "Günlük kasa takibi",
        "Gelir-gider raporları",
        "Ödeme türü analizi (nakit, kart, havale)",
        "Personel bazında satış raporları",
        "Hizmet karlılık analizi",
        "Vergi beyannamesi hazırlık"
      ]
    },
    {
      id: 4,
      title: "Personel Yönetimi",
      icon: "⭐",
      description: "Ekibinizi etkili şekilde yönetin ve motive edin",
      features: [
        "Personel performans takibi",
        "Çalışma saatleri ve vardiya planlaması",
        "Komisyon hesaplamaları",
        "Yetki ve rol yönetimi",
        "Personel bazında müşteri değerlendirmeleri",
        "Eğitim ve gelişim takibi"
      ]
    },
    {
      id: 5,
      title: "Envanter Yönetimi",
      icon: "📦",
      description: "Stok ve malzemelerinizi kontrol altında tutun",
      features: [
        "Ürün ve malzeme stok takibi",
        "Tedarikçi yönetimi",
        "Otomatik stok uyarıları",
        "Satış ve kullanım raporları",
        "Maliyet analizi",
        "Seri takip sistemi"
      ]
    },
    {
      id: 6,
      title: "Raporlama ve Analiz",
      icon: "📊",
      description: "Verilerinizi anlamlı raporlara dönüştürün",
      features: [
        "Günlük, haftalık, aylık raporlar",
        "Gelir trendi analizi",
        "Müşteri davranış analizi",
        "Hizmet popülaritesi raporları",
        "Personel verimliliği raporları",
        "Karşılaştırmalı dönem analizleri"
      ]
    },
    {
      id: 7,
      title: "Online Randevu Sistemi",
      icon: "🌐",
      description: "Müşterileriniz 7/24 randevu alabilsin",
      features: [
        "Web sitesi entegrasyonu",
        "Mobil uyumlu arayüz",
        "Gerçek zamanlı müsaitlik kontrolü",
        "Otomatik onay sistemi",
        "Online ödeme desteği",
        "Sosyal medya entegrasyonu"
      ]
    },
    {
      id: 8,
      title: "İletişim ve Bildirimler",
      icon: "📱",
      description: "Müşterilerinizle sürekli iletişimde kalın",
      features: [
        "SMS bildirim sistemi",
        "Email pazarlama kampanyaları",
        "WhatsApp entegrasyonu",
        "Otomatik hatırlatmalar",
        "Doğum günü mesajları",
        "Promosyon duyuruları"
      ]
    },
    {
      id: 9,
      title: "Güvenlik ve Yedekleme",
      icon: "🔒",
      description: "Verileriniz güvende, her zaman erişilebilir",
      features: [
        "256-bit SSL şifreleme",
        "Otomatik veri yedekleme",
        "Kullanıcı yetki yönetimi",
        "İşlem logları",
        "KVKK uyumlu veri saklama",
        "Bulut tabanlı güvenli depolama"
      ]
    }
  ], []);

  return (
    <div className="features-page">
      <Helmet>
        <title>Randevu345 - Salon Yönetiminde Yeni Dönem</title>
        <meta name="description" content="Randevu345'in güçlü özellikleri ile salon işletmeciliğinin her detayını profesyonel şekilde yönetin. Zamandan tasarruf edin, verimliliği artırın." />
      </Helmet>
      {/* Hero Section */}
      <section className="features-hero">
        <div className="container">
          <div className="features-hero-content">
            <h1 className="features-hero-title">
              Salon Yönetiminde 
              <span className="hero-gradient-text"> Yeni Dönem</span>
            </h1>
            <p className="features-hero-description">
              Randevu345'in güçlü özellikleri ile salon işletmeciliğinin her detayını 
              profesyonel şekilde yönetin. Zamandan tasarruf edin, verimliliği artırın.
            </p>
            <div className="features-stats">
              <div className="stat-item">
                <span className="stat-number">50+</span>
                <span className="stat-label">Özellik</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">99.9%</span>
                <span className="stat-label">Uptime</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">7/24</span>
                <span className="stat-label">Destek</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="features-grid-section">
        <div className="container">
          <div className="features-grid">
            {featureCategories.map((category, index) => (
              <FeatureCategory 
                key={category.id} 
                category={category} 
                index={index} 
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="features-cta">
        <div className="container">
          <div className="features-cta-content">
            <h2 className="features-cta-title">
              Tüm Bu Özellikleri <span className="cta-gradient-text">Ücretsiz</span> Deneyin
            </h2>
            <p className="features-cta-description">
              30 gün ücretsiz deneme süresi ile Randevu345'in tüm özelliklerini keşfedin.
              Kredi kartı bilgisi gerektirmez.
            </p>
            <div className="features-cta-buttons">
              <a 
                href="https://play.google.com/store/apps/details?id=com.randevu345.app" 
                className="cta-button primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/assets/googleplay.svg" alt="Google Play'den İndirin" />
              </a>
              <a 
                href="https://apps.apple.com/tr/app/randevu345/id6743093049" 
                className="cta-button primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/assets/appstore.svg" alt="App Store'dan İndirin" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
});

export default FeaturesPage; 